'use client'

import { useEffect, useState } from 'react'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import type { EmailDraft } from '@/lib/email-draft'

interface EmailDraftViewProps {
  draft: EmailDraft
  /** Called after "Approve & Send" (which also fires the "Email sent" toast). */
  onSent?: () => void
  className?: string
}

/**
 * Shared presentation for a generated email draft: shows To / Subject / Body,
 * lets the user Edit them inline, and mocks "Approve & Send" with a toast.
 * Used by the Audit-Lifecycle kanban modal (EmailDraftModal) and the AI Manager
 * chat card (EmailDraftCard). No email is actually sent (safety: sending is a
 * user-initiated, mocked action).
 */
export function EmailDraftView({ draft, onSent, className }: EmailDraftViewProps) {
  const [editing, setEditing] = useState(false)
  const [subject, setSubject] = useState(draft.subject)
  const [body, setBody] = useState(draft.body)

  // Re-sync when a genuinely different draft is supplied (e.g. a different
  // finding). Keyed on content, not object identity, so parent re-renders that
  // rebuild the same draft inline don't wipe the user's in-progress edits.
  useEffect(() => {
    setSubject(draft.subject)
    setBody(draft.body)
    setEditing(false)
  }, [draft.subject, draft.body])

  const handleSend = () => {
    toast.success('Email sent', {
      description: `Sent to ${draft.to || 'the vendor'}`,
    })
    onSent?.()
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* To */}
      <div className="flex items-center gap-2 text-sm">
        <span className="w-16 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
          To
        </span>
        <span className="truncate font-medium text-gray-700">
          {draft.to || '—'}
        </span>
      </div>

      {/* Subject */}
      <div className="flex items-start gap-2 text-sm">
        <span className="w-16 shrink-0 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Subject
        </span>
        {editing ? (
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800 focus:border-brand-cornflower focus:outline-none focus:ring-1 focus:ring-brand-cornflower"
          />
        ) : (
          <span className="flex-1 pt-1.5 font-semibold text-gray-800">
            {subject}
          </span>
        )}
      </div>

      {/* Body */}
      <div>
        {editing ? (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="w-full resize-y rounded-xl border border-gray-200 p-3 text-sm leading-relaxed text-gray-700 focus:border-brand-cornflower focus:outline-none focus:ring-1 focus:ring-brand-cornflower"
          />
        ) : (
          <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm leading-relaxed text-gray-700">
            {body}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={() => setEditing((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-brand-cornflower hover:text-brand-navy"
        >
          <Icons.edit className="h-4 w-4" />
          {editing ? 'Done editing' : 'Edit'}
        </button>
        <button
          onClick={handleSend}
          className="flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-navy/90"
        >
          <Icons.send className="h-4 w-4" />
          Approve &amp; Send
        </button>
      </div>
    </div>
  )
}
